import classNames from 'classnames';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { ModelType } from '../../common/enums.js';
import {
  corporateCustomersMsg,
  frequentlyAskedQuestionsMsg,
  internetSubscriptionsMsg,
  mobileM2MMsg,
  subscriptionsMsg,
  t,
} from '../../common/i18n';
import { AnalyticsEventOrigins } from '../../common/utils/analyticsUtils';
import { AppStoreBadges } from '../AppStoreBadges/AppStoreBadges.js';
import { BreadCrumbs } from '../BreadCrumbs';
import { FaqAccordion } from '../FaqAccordion/FaqAccordion.js';
import { SubscriptionCardsByModelTypeWrapper } from '../OrderSubscriptionSelection/SubscriptionCardsByModelTypeWrapper.js';
import { Picture } from '../Picture/Picture';
import { pictureStorageBaseUrl } from '../Picture/contentfulImageProxy';
import { PublicSelfServiceBanner } from '../PublicSelfServiceBanner/PublicSelfServiceBanner.js';

import './PublicM2MSubscriptions.scss';

const BASE_CLASS = 'of-public-m2m-subscriptions';
const BLEED_GREY = `${BASE_CLASS}__bleed-grey`;

const APP_COMPATIBILITY_FOOTNOTE_ID = 'app_compatibility_footnote';
const IOT_DEVICES_ID = 'iot_devices';
const LAITENETTI_APP_ID = 'laitenetti_app';
const LAITENETTI_SUBSCRIPTION_FEATURES_ID = 'laitenetti_subscription_features';

export const PublicMobileM2MBreadcrumbs = () => (
  <BreadCrumbs
    breadCrumbPaths={[
      { name: t.KBLX(corporateCustomersMsg), path: '/' },
      { name: t.JPRH(subscriptionsMsg), path: paths.SUBSCRIPTIONS_PUBLIC },
      { name: t.SEYV(internetSubscriptionsMsg), path: paths.INTERNET_SUBSCRIPTIONS_PUBLIC },
      { name: t.A7CA(mobileM2MMsg) },
    ]}
  />
);

export const PublicM2MHeader = () => (
  <header>
    <h1>{t.OD27('Laitenetti for businesses – Internet for remotely controlled smart devices')}</h1>
    <div className={dsClass.TEXT_LEAD}>
      {t.OD28(
        'Elisa Laitenetti enables data collection and remote control over the Internet. It allows you to connect via the internet to your company’s remotely controlled smart devices, such as surveillance cameras, the alarm system or heating devices, and collect data remotely.'
      )}
    </div>
    <div>
      {t.OD29(
        'Do you want to receive a video image from the surveillance cameras or control the air-source heat pump remotely?'
      )}
    </div>
    <div>
      {t.OD30(
        'Elisa Laitenetti is quick and easy to deploy. Choose a suitable subscription for your company and order Laitenetti with fast delivery!'
      )}
    </div>
    <div>
      <a href={`#${IOT_DEVICES_ID}`}>{t.OD31('Also check out IoT devices')}</a>.
    </div>
  </header>
);

const NORMAL_PRICED_CALLS_PRICE = '0,113';
const INCLUDED_SMS_MMS = '100';
const EXTRA_SMS = '0,073';
const EXTRA_MMS = '0,155';
const ROAMING_MONTHS = '3';

export const LaitenettiSubscriptionFeatures = () => {
  return (
    <section id={LAITENETTI_SUBSCRIPTION_FEATURES_ID} className={BLEED_GREY}>
      <h2>{t.B104('Laitenetti subscription features')}</h2>
      <div>
        <h3>{t.B105('Calls and texts')}</h3>
        <ul>
          <li>
            {t.B106(
              'Calls to normal-priced domestic landline and mobile subscriptions {} € per minute.',
              NORMAL_PRICED_CALLS_PRICE
            )}
          </li>
          <li>{t.B107('The monthly subscription price includes {} SMS/MMS messages', INCLUDED_SMS_MMS)}</li>
          <li>{t.B108('Text messages beyond the amount included in the package {} € each', EXTRA_SMS)}</li>
          <li>{t.B109('MMS messages beyond the amount included in the package {} € each', EXTRA_MMS)}</li>
          <li>{t.B10A('For call, SMS and mobile payment blocking, general block P1 is enabled by default.')}</li>
        </ul>
      </div>
      <div>
        <h3>{t.B10B('Internet connection')}</h3>
        <ul>
          <li>{t.B10C('Unlimited Internet in Finland')}</li>
          <li>{t.B10D('Works in the 2G and 4G networks, Laitenetti 5G also works in the 5G network')}</li>
          <li>
            {t.B10E(
              'Elisa Laitenetti subscriptions are only intended for temporary roaming abroad for up to {} months. Elisa has the right to monitor the use of subscriptions and, if necessary, prevent permanent use abroad. ',
              ROAMING_MONTHS
            )}
            <a href={paths.USING_YOUR_PHONE_ABROAD}>{t.B0MB('Read more about prices and access abroad')}</a>.
          </li>
        </ul>
        <h3>{t.B10F('Terms and conditions')}</h3>
        <ul>
          <li>{t.B10G('No fixed-term agreements')}</li>
          <li>{t.B10H('You can terminate your subscription at any time')}</li>
        </ul>
      </div>
    </section>
  );
};

export const ElisaLaitenettiApp = () => {
  return (
    <section id={LAITENETTI_APP_ID}>
      <div>
        <h2>{t.OD32('The free of charge Elisa Laitenetti app tracks your car or belongings')}</h2>
        <h3 className={dsClass.H4}>
          {t.OD33('The Laitenetti app is compatible with your Elisa Laitenetti subscription and a tracker.')}
        </h3>
        <ul>
          <li>
            {t.OD34(
              'View the location of your trackers on the map – the information is updated in your phone in real time regardless of the distance'
            )}
          </li>
          <li>{t.OD35('View your location history and the speed data of vehicles, for example')}</li>
          <li>{t.OD36('Share your location data with other people if you want')}</li>
          <li>{t.OD37('You can choose from several map layouts, such as a terrain map')}</li>
          <li>{t.OD38('All data is forwarded securely to Elisa')}</li>
        </ul>
        <h3 aria-describedby={APP_COMPATIBILITY_FOOTNOTE_ID} className={dsClass.H4}>
          {t.OD39('Download the app for free from your app store!')}
        </h3>
        <p className={`${BASE_CLASS}__footnote`} id={APP_COMPATIBILITY_FOOTNOTE_ID}>
          {t.OD40(`The app is only compatible with trackers that use Elisa's Laitenetti subscription.`)}
        </p>
        <AppStoreBadges
          apple={{ href: 'https://apps.apple.com/fi/app/elisa-paikannus/id1605935517' }}
          google={{ href: 'https://play.google.com/store/apps/details?id=com.elisa.paikannus' }}
        />
      </div>
      <Picture
        alt=""
        offerWidthAlternatives={[223, 288, 351, 400, 800]}
        renderedImageSize={{
          onPhone: 'min(400px, 100vw - 32px)',
          onPhoneLarge: '400px',
          onLaptop: 'min(400px, (100vw - 64px - 136px) * 33 / 113)',
          onDesktop: 'calc((1320px - 136px) * 33) / 113)',
        }}
        src={`${pictureStorageBaseUrl}s3GaA0iWh8Ff2oB3QtEM4/i.jpg`}
      />
    </section>
  );
};

export const IoTDevices = () => {
  return (
    <aside id={IOT_DEVICES_ID}>
      <h2>{t.ZQ1A('Order IoT devices easily online')}</h2>
      <p>
        {t.ZQ1B(
          `Explore the online store's IoT devices, such as security cameras and trail cameras, alarm systems, and remotely readable thermometers.`
        )}
      </p>
      <a
        className={classNames(
          dsClass.LINK,
          dsClass.LINK_STYLE_LINK_BUTTON,
          dsClass.LINK_BUTTON_COLOR_LIGHT,
          dsClass.LINK_BUTTON_SIZE_L,
          dsClass.MARGIN_BOTTOM_8,
          dsClass.MARGIN_TOP_4
        )}
        href={paths.IOT_DEVICES}
      >
        {t.OD23('View all IoT devices')}
      </a>
    </aside>
  );
};

export const DisjointedGenericInformationAboutLaitenetti = () => {
  return (
    <section className={classNames(BLEED_GREY, dsClass.TEXT_ALIGN_LEFT)}>
      {t.OD22(
        'The Laitenetti subscription allows you to access and control your smart devices from any device with an internet connection, such as a smartphone or desktop computer. You can view and collect data over the Internet and control and manage your devices remotely. Access Point Name (APN) for Laitenetti business subscriptions is APN internet, cannot connect other APNs.'
      )}
    </section>
  );
};

export const FAQ = () => (
  <FaqAccordion
    heading={t.SDWG(frequentlyAskedQuestionsMsg)}
    items={[
      {
        content: (
          <>
            <p>
              {t.OD1O(
                'Laitenetti is an internet subscription for the management of remotely controlled devices, such as surveillance cameras and trackers or smart sockets and thermometers.'
              )}
            </p>
            <p>
              {t.OD1P(
                'Laitenetti works like a conventional subscription, establishing a connection with a device. A device subscription enables you to monitor and remotely control devices or view the images or video sent by a camera. Laitenetti is intended for a data connection between devices.'
              )}
            </p>
          </>
        ),
        heading: t.OD1N('What is Laitenetti?'),
      },
      {
        content: (
          <p>
            {t.OD1R(
              'The Laitenetti subscription is compatible with devices such as alarms, air source heat pumps, GPS trackers, condition monitoring sensors, warning devices, surveillance and trail cameras as well as mobile routers and network devices.'
            )}
          </p>
        ),
        heading: t.OD1Q('What devices are compatible with the Laitenetti subscription?'),
      },
      {
        content: (
          <p>
            {t.OD1T(
              'Trail and surveillance cameras require slightly faster upstream and downstream speeds, which is why we recommend an Elisa Laitenetti M, Laitenetti L or Laitenetti 5G subscriptions. Laitenetti L and Laitenetti 5G are suitable for a camera that streams live video while Laitenetti M is sufficient for sending images.'
            )}
          </p>
        ),
        heading: t.OD1S('Which subscription is compatible with a surveillance or trail camera?'),
      },
      {
        content: (
          <p>
            {t.OD1V(
              'When you need to stream high-quality live video, we recommend using the Laitenetti 5G subscription.'
            )}
          </p>
        ),
        heading: t.OD1U('Which interface is suitable for streaming high-quality live video?'),
      },
      {
        content: (
          <>
            <p>
              {t.OD1X(
                'With the Elisa Laitenetti app, you can do things like view the locations of your trackers on the map or monitor the speed data of vehicles. If you want, you can share your location data with other people.'
              )}
            </p>
            <p>
              {t.OD1Y(
                'The Laitenetti app works together with a device subscription and tracking device. You can download the app for free from your app store. Please note that the app only works with trackers that use Elisa’s Laitenetti subscription.'
              )}
            </p>
          </>
        ),
        heading: t.OD1W('How can I track my device?'),
      },
      {
        content: (
          <>
            <p>
              {t.OD20(
                'When you have made the order, we will send you a new SIM card by post that you can install in your smart device. The delivery of the SIM card will take approximately 2 to 4 business days.'
              )}
            </p>
            <p>
              {t.OD21('See ')}
              <a href={paths.INSTRUCTIONS_IOT_SUBSCRIPTIONS}>
                {t.SF20(`instructions for using Elisa's Laitenetti subscriptions (in Finnish)`)}
              </a>
              .
            </p>
          </>
        ),
        heading: t.OD1Z('How do I start using my Elisa Laitenetti subscription?'),
      },
    ]}
  />
);

export const AreYouAConsumerCustomer = () => (
  <section className={BLEED_GREY}>
    <h2 className={dsClass.H3}>{t.B100('Are you a consumer customer?')}</h2>
    <a className={dsClass.TEXT_L} href="https://elisa.fi/laitesim/">
      {t.B101('See the Laitenetti options for consumers')}
    </a>
  </section>
);

export const PublicM2MSubscriptions = () => {
  return (
    <section className={`${BASE_CLASS} of-public-page`}>
      <PublicMobileM2MBreadcrumbs />
      <PublicM2MHeader />
      <SubscriptionCardsByModelTypeWrapper
        onlineModelType={ModelType.MobileM2M}
        eventOrigin={AnalyticsEventOrigins.PUBLIC}
      />
      <PublicSelfServiceBanner />
      <LaitenettiSubscriptionFeatures />
      <ElisaLaitenettiApp />
      <IoTDevices />
      <DisjointedGenericInformationAboutLaitenetti />
      <FAQ />
      <AreYouAConsumerCustomer />
    </section>
  );
};
